import {
  isCameraCalibrationMessage,
  isCaptureStatusMessage,
  isPackedLayoutMessage,
  isPoseFrameMessage,
  isPoseSourceApplied,
  isRtcSignal,
  type CameraCalibrationMessage,
  type CaptureStatusMessage,
  type PoseFrameMessage,
  type PackedLayoutMessage,
  type PoseSource,
  type PoseSourceRequest,
  type RtcSignal,
} from '../protocol.ts';
import { relaySignal, type SignalMessage } from '../signalling.ts';
import type { RtcStreamMeasurement } from './rtcStats.ts';
import { startRtcStatsPolling } from './rtcStatsPoller.ts';

export interface FloorPeerCallbacks {
  onStream: (stream: MediaStream, track: MediaStreamTrack) => void;
  onPose: (frame: PoseFrameMessage) => void;
  onInstallation: (installation: CameraCalibrationMessage) => void;
  onCaptureStatus: (status: CaptureStatusMessage) => void;
  onPackedLayout: (layout: PackedLayoutMessage) => void;
  onPoseSourcePending: (source: PoseSource) => void;
  onPoseSourceApplied: (source: PoseSource) => void;
  onStopped: (reason: string) => void;
  onStats?: (measurement: RtcStreamMeasurement | undefined) => void;
}

export class FloorPeer {
  readonly #socket: WebSocket;
  readonly #callbacks: FloorPeerCallbacks;
  #peer?: RTCPeerConnection;
  #control?: RTCDataChannel;
  #telemetry?: RTCDataChannel;
  #queuedCandidates: RTCIceCandidateInit[] = [];
  #stopStats?: () => void;
  #desiredSource: PoseSource = 'data-channel';
  #appliedSource?: PoseSource;
  #revision = 1;

  constructor(socket: WebSocket, callbacks: FloorPeerCallbacks) {
    this.#socket = socket;
    this.#callbacks = callbacks;
  }

  setPoseSource(source: PoseSource): void {
    if (source === this.#desiredSource && source === this.#appliedSource) return;
    this.#desiredSource = source;
    this.#revision += 1;
    this.#appliedSource = undefined;
    this.#callbacks.onPoseSourcePending(source);
    this.#sendSourceRequest();
  }

  #sendSourceRequest(): void {
    if (this.#control?.readyState !== 'open') return;
    const request: PoseSourceRequest = {
      kind: 'pose-source-request',
      revision: this.#revision,
      source: this.#desiredSource,
    };
    this.#control.send(JSON.stringify(request));
  }

  #create(): RTCPeerConnection {
    this.#stopStats?.();
    this.#stopStats = undefined;
    this.#peer?.close();
    this.#control = undefined;
    this.#telemetry = undefined;
    this.#appliedSource = undefined;
    this.#callbacks.onPoseSourcePending(this.#desiredSource);
    const peer = new RTCPeerConnection({ iceServers: [] });
    peer.addEventListener('icecandidate', (event) => {
      if (event.candidate) relaySignal(this.#socket, 'camera', { kind: 'rtc-candidate', candidate: event.candidate.toJSON() } satisfies RtcSignal);
    });
    peer.addEventListener('track', (event) => {
      if (this.#peer !== peer) return;
      const stream = event.streams[0] ?? new MediaStream([event.track]);
      this.#callbacks.onStream(stream, event.track);
      event.track.addEventListener('ended', () => this.#callbacks.onStopped('Camera stopped'));
    });
    peer.addEventListener('datachannel', (event) => {
      if (this.#peer !== peer) return;
      if (event.channel.label === 'mirror-control') this.#acceptControl(event.channel);
      else if (event.channel.label === 'mirror-telemetry') this.#acceptTelemetry(event.channel);
    });
    peer.addEventListener('connectionstatechange', () => {
      if (this.#peer !== peer) return;
      if (peer.connectionState === 'failed' || peer.connectionState === 'closed') {
        this.#appliedSource = undefined;
        this.#callbacks.onPoseSourcePending(this.#desiredSource);
        this.#callbacks.onStopped('Peer-to-peer camera disconnected');
      }
    });
    this.#peer = peer;
    this.#stopStats = startRtcStatsPolling(peer, 'inbound', (measurement) => this.#callbacks.onStats?.(measurement));
    return peer;
  }

  #acceptControl(channel: RTCDataChannel): void {
    this.#control?.close();
    this.#control = channel;
    channel.addEventListener('open', () => {
      if (this.#control !== channel) return;
      this.#appliedSource = undefined;
      this.#callbacks.onPoseSourcePending(this.#desiredSource);
      this.#sendSourceRequest();
    });
    channel.addEventListener('message', (event) => {
      if (this.#control !== channel) return;
      try {
        const parsed: unknown = JSON.parse(String(event.data));
        if (isCameraCalibrationMessage(parsed)) {
          this.#callbacks.onInstallation(parsed);
          return;
        }
        if (isCaptureStatusMessage(parsed)) {
          if (this.#desiredSource === 'laptop-rederives' && this.#appliedSource === 'laptop-rederives') {
            this.#callbacks.onCaptureStatus(parsed);
          }
          return;
        }
        if (isPackedLayoutMessage(parsed)) {
          this.#callbacks.onPackedLayout(parsed);
          return;
        }
        if (!isPoseSourceApplied(parsed)
          || parsed.revision !== this.#revision
          || parsed.source !== this.#desiredSource) return;
        this.#appliedSource = parsed.source;
        this.#callbacks.onPoseSourceApplied(parsed.source);
      } catch {
        // Reliable peer control remains inert unless it matches a closed schema.
      }
    });
    channel.addEventListener('close', () => {
      if (this.#control !== channel) return;
      this.#appliedSource = undefined;
      this.#callbacks.onPoseSourcePending(this.#desiredSource);
    });
  }

  #acceptTelemetry(channel: RTCDataChannel): void {
    this.#telemetry?.close();
    this.#telemetry = channel;
    channel.addEventListener('message', (message) => {
      if (this.#telemetry !== channel
        || this.#desiredSource !== 'data-channel'
        || this.#appliedSource !== 'data-channel') return;
      try {
        const parsed: unknown = JSON.parse(String(message.data));
        if (isPoseFrameMessage(parsed)) this.#callbacks.onPose(parsed);
      } catch {
        // Lossy peer telemetry is untrusted input and never mutates state when malformed.
      }
    });
  }

  handle(message: SignalMessage): void {
    if (message.type === 'peer-left' && message.seat === 'camera') {
      this.close();
      this.#callbacks.onStopped('Camera left the room');
      return;
    }
    if (message.type !== 'signal' || message.from !== 'camera' || !isRtcSignal(message.data)) return;
    void this.#handleRtc(message.data).catch(() => this.#callbacks.onStopped('WebRTC negotiation failed'));
  }

  async #handleRtc(signal: RtcSignal): Promise<void> {
    if (signal.kind === 'rtc-candidate') {
      if (this.#peer?.remoteDescription) await this.#peer.addIceCandidate(signal.candidate);
      else this.#queuedCandidates.push(signal.candidate);
      return;
    }
    if (signal.description.type !== 'offer') return;
    const peer = this.#create();
    await peer.setRemoteDescription(signal.description);
    for (const candidate of this.#queuedCandidates.splice(0)) await peer.addIceCandidate(candidate);
    const answer = await peer.createAnswer();
    await peer.setLocalDescription(answer);
    relaySignal(this.#socket, 'camera', { kind: 'rtc-description', description: peer.localDescription!.toJSON() } satisfies RtcSignal);
  }

  close(): void {
    this.#stopStats?.();
    this.#stopStats = undefined;
    this.#control?.close();
    this.#telemetry?.close();
    this.#peer?.close();
    this.#control = undefined;
    this.#telemetry = undefined;
    this.#peer = undefined;
    this.#appliedSource = undefined;
    this.#queuedCandidates = [];
  }
}
